import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import DashboardBreadcrumbs from "../../components/DashboardBreadcrumbs";
import RendererConfig from "../../components/RendererDetails";
import AccessLockModal from "../../components/security/AccessLockModal";
import { Modal as OpusModal, Field as OpusField, TextInput as OpusTextInput, Select as OpusSelect, Toggle as OpusToggle, Button as OpusButton, Segmented as OpusSegmented, CardGridSkeleton } from "../../components/common/opus";
import CircularIndeterminate from "../../components/Loader.component";
import {
  AddCircle,
  ViewList,
  Reorder,
  GridView,
  Fullscreen,
  AspectRatio,
  Person,
  Group,
  AddIcon,
  RemoveIcon,
} from "@mui/icons-material";
import {
  Divider,
  Switch,
  Typography,
  Box,
  Button,
  Checkbox,
  Modal,
  TextField,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Grid,
  ButtonGroup,
  IconButton,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import Add from "@mui/icons-material/Add";
import Remove from "@mui/icons-material/Remove";
import { useAlert } from "../../contexts/AlertContext";
import { useActivity } from "../../contexts/ActivityTrackingContext";
import { useSession } from "../../contexts/SessionContext";
import { usePermissions } from "../../contexts/PermissionsContext";
import apiService from "../../services/api.service";

// ✅ Helper functions inline
const createSceneLookupMap = (scenes) => {
  if (!Array.isArray(scenes)) return {};

  const sceneMap = {};
  scenes.forEach((scene) => {
    if (scene.id) {
      sceneMap[scene.id] = {
        id: scene.id,
        name: scene.name,
        scene_path: scene.scene_path,
        load_part_path: scene.load_part_path,
        captions: scene.captions || [],
      };
    }
  });

  return sceneMap;
};

const mergeRenderersWithScenes = (renderers, sceneMap) => {
  if (!Array.isArray(renderers)) return [];
  if (!sceneMap || typeof sceneMap !== "object") return renderers;

  return renderers.map((renderer) => {
    const sceneData = renderer.scene_id ? sceneMap[renderer.scene_id] : null;

    return {
      ...renderer,
      scene_name: sceneData?.name || null,
      scene_path: sceneData?.scene_path || null,
      load_part_path: sceneData?.load_part_path || null,
      sceneData: sceneData || null,
    };
  });
};

const calculateOptimalGridColumns = (count) => {
  if (count <= 1) return 1;
  if (count <= 4) return 2;
  if (count <= 9) return 3;
  if (count <= 16) return 4;
  return 5;
};

const RendererManagement = () => {
  const { userInfo } = useContext(AuthContext);
  const userId = userInfo?.data?._id;
  const userRole = userInfo?.data?.role?.name;
  const userOrg = userInfo?.data?.organization_id?._id;
  const isSuperAdmin = userRole === "super_admin";
  const { can } = usePermissions();
  const canEditRenderer = can("renderers.update");
  const canDeleteRenderer = can("renderers.delete");
  const canCreateRenderer = can("renderers.create");

  const [editMode, setEditMode] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [selectedRenderer, setSelectedRenderer] = useState([]);
  // OPUS-1045: multiviewer tile selection — which renderers show in the gridView
  // multiviewer. Persisted per org (empty = show all). multiviewerEditMode shows
  // per-tile checkboxes so the operator can build the layout.
  const [multiviewerEditMode, setMultiviewerEditMode] = useState(false);
  const [multiviewerSelection, setMultiviewerSelection] = useState([]);
  // OPUS-1148: named multiview presets (UI-side, per org). Save the current
  // selection as a preset; load one to jump straight to that multiview.
  const [multiviewerPresets, setMultiviewerPresets] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  // Open the Add Renderer modal when arriving from the Dashboard's "New
  // Renderer" button (navigate(..., { state: { openAddRenderer: true } })).
  // Ref-guarded so it fires once and a refresh/back doesn't re-open it.
  const location = useLocation();
  const addNavHandledRef = useRef(false);
  useEffect(() => {
    if (location.state?.openAddRenderer && !addNavHandledRef.current) {
      addNavHandledRef.current = true;
      setOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  const [organizations, setOrganizations] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [sceneMap, setSceneMap] = useState({});
  const [filteredRenderers, setFilteredRenderers] = useState([]);
  const [presence, setPresence] = useState({}); // OPUS-913: rendererId -> [{userId,name,since}]
  const [lockModal, setLockModal] = useState({ open: false, id: null, name: "" }); // OPUS-913 access lock
  // OPUS-913: owner edit-lock ("lock to me") — owner/super-admin toggles it on the card.
  const handleToggleOwnerLock = async (renderer, shouldLock) => {
    try {
      const res = shouldLock
        ? await apiService.lockRenderer(renderer.renderer_id)
        : await apiService.unlockRenderer(renderer.renderer_id);
      // Patch from the server's actual response, not just the optimistic intent —
      // the request can resolve without the lock state actually changing (e.g. a
      // 200 wrapping a no-op), so trust what the backend says was applied.
      const serverLock = res?.data;
      const patch = serverLock
        ? {
            locked: !!serverLock.locked,
            locked_by_user_id: serverLock.locked ? (serverLock.locked_by_user_id ?? userId) : null,
            locked_by_name: serverLock.locked ? (serverLock.locked_by_name ?? (userInfo?.data?.name || userInfo?.data?.user_name || null)) : null,
          }
        : (shouldLock
          ? { locked: true, locked_by_user_id: userId, locked_by_name: userInfo?.data?.name || userInfo?.data?.user_name || null }
          : { locked: false, locked_by_user_id: null, locked_by_name: null });
      setFilteredRenderers((prev) => prev.map((r) => (r.renderer_id === renderer.renderer_id ? { ...r, owner_lock: patch } : r)));
      setAllRenderers((prev) => prev.map((r) => (r.renderer_id === renderer.renderer_id ? { ...r, owner_lock: patch } : r)));
      alert.success(patch.locked ? "Renderer locked to you" : "Renderer unlocked");
    } catch (e) { alert.error(e?.message || "Lock action failed"); }
  };
  const { selectedOrgId: sessionOrgId, updateSelectedOrgId } = useSession();
  const [selectedOrgId, setSelectedOrgId] = useState(sessionOrgId || "");
  // OPUS-913: follow the global sidebar org selector (the per-page selector moved there).
  useEffect(() => {
    if (sessionOrgId && sessionOrgId !== selectedOrgId) setSelectedOrgId(sessionOrgId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionOrgId]);

  // OPUS-913: live presence — batch-poll who is viewing each visible renderer.
  // OPUS-913 perf: key on the stable, sorted id-set string so the 15s poll only
  // resets when the actual renderer id set changes — not on every render/filter
  // that re-creates the filteredRenderers array (which previously fired an extra
  // immediate poll + tore down the interval each time).
  const presenceIdsKey = useMemo(
    () => (filteredRenderers || []).map((r) => r.renderer_id).filter(Boolean).sort().join(","),
    [filteredRenderers],
  );
  useEffect(() => {
    const ids = presenceIdsKey ? presenceIdsKey.split(",") : [];
    if (!ids.length) { setPresence({}); return undefined; }
    let cancelled = false;
    const poll = () => {
      if (typeof document !== "undefined" && document.hidden) return; // skip polling while tab is hidden
      apiService.getRendererPresenceBatch(ids)
        .then((resp) => { if (!cancelled) setPresence(resp?.data || resp || {}); })
        .catch(() => {});
    };
    poll();
    const iv = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presenceIdsKey]);
  const alert = useAlert();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [allRenderers, setAllRenderers] = useState([]);

  const [dataStatus, setDataStatus] = useState({
    orgs: false,
    renderers: false,
    scenes: false,
  });
  const [showOnlyMyRenderers, setShowOnlyMyRenderers] = useState(false);
  const [userToggledFilter, setUserToggledFilter] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSceneFilter, setSelectedSceneFilter] = useState("all");
  // OPUS-1045: sort + creator filter for the renderer list.
  const [sortBy, setSortBy] = useState("newest");
  const [creatorFilter, setCreatorFilter] = useState("all");
  const creatorOptions = useMemo(() => {
    const seen = new Map();
    (allRenderers || []).forEach((r) => {
      const id = r.created_by?.id || (typeof r.created_by === "string" ? r.created_by : null);
      if (id && !seen.has(id)) seen.set(id, r.created_by?.name || r.created_by?.user_name || id);
    });
    return [{ value: "all", label: "All creators" }, ...Array.from(seen, ([value, label]) => ({ value, label }))];
  }, [allRenderers]);
  const [hasUserRenderers, setHasUserRenderers] = useState(false);

  const [gridColumns, setGridColumns] = useState(2);
  const multiViewRef = useRef();
  const { trackActivity, ACTIVITY_TYPES, RESOURCE_TYPES } = useActivity();

  const [newRenderer, setNewRenderer] = useState({
    label: "",
    renderer_type: "Aero",
    renderer_status: "inactive", // ✅ NEW
    aspect_ratio: "16:9",
    resolution: "1920x1080", // ✅ NEW
    frame_rate: 60, // ✅ NEW
    scene_id: "",
    create_preview_renderer: false,
  });

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleFilterChange = (showOnlyMine) => {
    setShowOnlyMyRenderers(showOnlyMine);
    setUserToggledFilter(true);

    if (showOnlyMine) {
      const userRenderers = allRenderers.filter(
        (renderer) =>
          renderer.created_by?.id === userId || renderer.created_by === userId,
      );

      if (userRenderers.length === 0) {
        alert.info(`You don't have any renderers associated with your account`);
        setShowOnlyMyRenderers(false);
        return;
      }
    }

    trackActivity(
      ACTIVITY_TYPES.READ,
      RESOURCE_TYPES.RENDERER,
      "filter",
      `${showOnlyMine ? "Showing" : "Not showing"} only my renderers`,
    );
  };

  useEffect(() => {
    if (!userToggledFilter && dataStatus.renderers && allRenderers.length > 0) {
      const hasMyRenderers = allRenderers.some(
        (renderer) =>
          renderer.created_by?.id === userId || renderer.created_by === userId,
      );

      if (hasMyRenderers && allRenderers.length > 1) {
        setShowOnlyMyRenderers(true);
      }
      setUserToggledFilter(true);
    }
  }, [dataStatus.renderers, allRenderers, userId, userToggledFilter]);

  useEffect(() => {
    if (dataStatus.orgs && dataStatus.renderers && dataStatus.scenes) {
      setPageLoading(false);
    }
  }, [dataStatus]);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const data = await apiService.getOrganizationsFromScene();
        setOrganizations(data);

        if (!isSuperAdmin && userOrg) {
          setSelectedOrgId(userOrg);
          updateSelectedOrgId(userOrg);
        } else if (
          sessionOrgId &&
          data.some((org) => org._id === sessionOrgId)
        ) {
          setSelectedOrgId(sessionOrgId);
        } else if (data && data.length > 0) {
          setSelectedOrgId(data[0]._id);
          updateSelectedOrgId(data[0]._id);
        }
        setDataStatus((prev) => ({ ...prev, orgs: true }));
      } catch (error) {
        console.error("Error fetching organizations:", error);
        setDataStatus((prev) => ({ ...prev, orgs: true }));
      }
    };

    fetchOrganizations();
  }, [isSuperAdmin, userOrg, sessionOrgId, updateSelectedOrgId]);

  // ✅ Fetch both renderers and scenes in parallel
  useEffect(() => {
    const loadData = async () => {
      if (!selectedOrgId) return;

      setLoading(true);

      try {
        const [renderersResponse, scenesResponse] = await Promise.all([
          apiService.getRenderersByOrganization(selectedOrgId),
          apiService.getScenesByOrganization(selectedOrgId),
        ]);

        const renderers =
          renderersResponse?.data?.renderers ||
          renderersResponse?.renderers ||
          renderersResponse ||
          [];

        const scenesData = scenesResponse?.scenes || scenesResponse || [];

        console.log("📦 Fetched renderers:", renderers.length);
        console.log("🎬 Fetched scenes:", scenesData.length);

        const sceneLookup = createSceneLookupMap(scenesData);
        setSceneMap(sceneLookup);
        setScenes(scenesData);

        const mergedRenderers = mergeRenderersWithScenes(
          renderers,
          sceneLookup,
        );

        console.log("✅ Merged renderers:", mergedRenderers.length);

        const userRenderers = mergedRenderers.filter(
          (renderer) =>
            renderer.created_by?.id === userId ||
            renderer.created_by === userId,
        );

        setHasUserRenderers(userRenderers.length > 0);
        setAllRenderers(mergedRenderers);
        setDataStatus((prev) => ({ ...prev, renderers: true, scenes: true }));
      } catch (error) {
        console.error("Error loading data:", error);
        alert.error("Failed to load renderers.");
        setAllRenderers([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedOrgId, userId]); // ✅ REMOVED 'alert' dependency

  useEffect(() => {
    if (!allRenderers.length) {
      setFilteredRenderers([]);
      return;
    }

    let filtered = [...allRenderers];

    if (showOnlyMyRenderers && userId) {
      const userRenderers = filtered.filter(
        (renderer) =>
          renderer.created_by?.id === userId || renderer.created_by === userId,
      );

      if (userRenderers.length === 0) {
        setShowOnlyMyRenderers(false);
        alert.info("You don't have any renderers associated with your account");
      } else {
        filtered = userRenderers;
      }
    }

    if (selectedSceneFilter !== "all") {
      filtered = filtered.filter(
        (renderer) =>
          renderer.scene_id === selectedSceneFilter ||
          renderer.sceneData?.id === selectedSceneFilter,
      );
    }

    // OPUS-1045: filter by creator + sort by time/name/creator/scene.
    if (creatorFilter !== "all") {
      filtered = filtered.filter(
        (r) => (r.created_by?.id || r.created_by) === creatorFilter,
      );
    }
    const ts = (r) => new Date(r.created_at || r.createdAt || 0).getTime();
    const creatorOf = (r) => String(r.created_by?.name || r.created_by?.user_name || r.created_by || "");
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "oldest": return ts(a) - ts(b);
        case "name": return String(a.renderer_name || "").localeCompare(String(b.renderer_name || ""));
        case "creator": return creatorOf(a).localeCompare(creatorOf(b));
        case "scene": return String(a.scene_name || "").localeCompare(String(b.scene_name || ""));
        case "newest":
        default: return ts(b) - ts(a);
      }
    });

    setFilteredRenderers(filtered);
  }, [allRenderers, showOnlyMyRenderers, selectedSceneFilter, creatorFilter, sortBy, userId]); // ✅ REMOVED 'alert'

  useEffect(() => {
    if (viewMode === "gridView" && filteredRenderers?.length) {
      const optimal = calculateOptimalGridColumns(filteredRenderers.length);
      setGridColumns(optimal);
    }
  }, [filteredRenderers?.length, viewMode]);

  // OPUS-1045: persist the multiviewer selection per org so the layout survives
  // reloads. Load on org change, save on selection change.
  useEffect(() => {
    if (!selectedOrgId) { setMultiviewerSelection([]); return; }
    try {
      const raw = localStorage.getItem(`dashboard:multiviewer:${selectedOrgId}`);
      setMultiviewerSelection(raw ? JSON.parse(raw) : []);
    } catch { setMultiviewerSelection([]); }
  }, [selectedOrgId]);
  useEffect(() => {
    if (!selectedOrgId) return;
    try { localStorage.setItem(`dashboard:multiviewer:${selectedOrgId}`, JSON.stringify(multiviewerSelection)); } catch { /* ignore */ }
  }, [multiviewerSelection, selectedOrgId]);

  // OPUS-1148: multiview presets — load on org change; save/load by name.
  useEffect(() => {
    if (!selectedOrgId) { setMultiviewerPresets([]); return; }
    try {
      const raw = localStorage.getItem(`dashboard:multiviewer-presets:${selectedOrgId}`);
      setMultiviewerPresets(raw ? JSON.parse(raw) : []);
    } catch { setMultiviewerPresets([]); }
  }, [selectedOrgId]);
  const persistPresets = (next) => {
    setMultiviewerPresets(next);
    try { localStorage.setItem(`dashboard:multiviewer-presets:${selectedOrgId}`, JSON.stringify(next)); } catch { /* ignore */ }
  };
  const handleSavePreset = () => {
    if (!multiviewerSelection.length) return;
    const name = (window.prompt("Save this multiview as a preset — name:") || "").trim();
    if (!name) return;
    persistPresets([...multiviewerPresets.filter((p) => p.name !== name), { name, rendererIds: multiviewerSelection }]);
  };
  const handleLoadPreset = (name) => {
    const preset = multiviewerPresets.find((p) => p.name === name);
    if (preset) { setMultiviewerSelection(preset.rendererIds || []); setMultiviewerEditMode(false); }
  };

  const handleOrgSelect = (event) => {
    const newOrgId = event.target.value;
    setSelectedOrgId(newOrgId);
    updateSelectedOrgId(newOrgId);
    setDataStatus((prev) => ({ ...prev, renderers: false, scenes: false }));
    setSelectedSceneFilter("all");
  };

  const handleRendererSelection = (e, id) => {
    setSelectedRenderer((prev) =>
      e.target.checked
        ? [...prev, id]
        : prev.filter((rendererId) => rendererId !== id),
    );
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleSceneFilterChange = (sceneId) => {
    setSelectedSceneFilter(sceneId);
  };

  const handleBulkDeleteRenderers = async () => {
    if (selectedRenderer.length === 0) return;

    setDeleteLoading(true);
    try {
      const renderersBeingDeleted = filteredRenderers.filter((renderer) =>
        selectedRenderer.includes(renderer.renderer_id),
      );

      const rendererIdsToDelete = renderersBeingDeleted.map(
        (renderer) => renderer.renderer_id,
      );

      const deletePromises = rendererIdsToDelete.map((rendererId) =>
        apiService.deleteRenderer(rendererId),
      );

      const results = await Promise.allSettled(deletePromises);

      const successfulResults = results
        .map((result, index) => {
          if (result.status === "fulfilled") {
            return renderersBeingDeleted[index];
          }
          return null;
        })
        .filter(Boolean);

      const successful = successfulResults.length;
      const failed = rendererIdsToDelete.length - successful;

      if (successful > 0) {
        successfulResults.forEach((renderer) => {
          trackActivity(
            ACTIVITY_TYPES.DELETE,
            RESOURCE_TYPES.RENDERER,
            renderer.renderer_id,
            renderer.renderer_name,
          );
        });

        const [renderersResponse, scenesResponse] = await Promise.all([
          apiService.getRenderersByOrganization(selectedOrgId),
          apiService.getScenesByOrganization(selectedOrgId),
        ]);

        const renderers =
          renderersResponse?.data?.renderers ||
          renderersResponse?.renderers ||
          [];
        const scenesData = scenesResponse?.scenes || [];
        const sceneLookup = createSceneLookupMap(scenesData);
        const mergedRenderers = mergeRenderersWithScenes(
          renderers,
          sceneLookup,
        );

        setAllRenderers(mergedRenderers);
        setSelectedRenderer([]);

        alert.success(
          `Successfully deleted ${successful} renderer${successful !== 1 ? "s" : ""}${failed > 0 ? `. Failed to delete ${failed}.` : ""}`,
        );
      } else {
        alert.error("Failed to delete any renderers. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting renderers:", error);
      alert.error("An error occurred while deleting renderers");
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleOpenDeleteDialog = () => {
    if (selectedRenderer.length === 0) return;
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewRenderer((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    const payload = {
      label: newRenderer.label,
      renderer_type: newRenderer.renderer_type,
      renderer_status: newRenderer.renderer_status,

      organization_id: selectedOrgId,

      technical_specs: {
        aspect_ratio: newRenderer.aspect_ratio,
        resolution: newRenderer.resolution,
        frame_rate: Number(newRenderer.frame_rate),
      },

      scene_id: newRenderer.scene_id || null,
      create_preview_renderer: newRenderer.create_preview_renderer,

      created_by: userInfo?.data?._id,
    };

    try {
      await apiService.createRenderer(payload);

      // ✅ Refresh data after creation
      const [renderersResponse, scenesResponse] = await Promise.all([
        apiService.getRenderersByOrganization(selectedOrgId),
        apiService.getScenesByOrganization(selectedOrgId),
      ]);

      const renderers =
        renderersResponse?.data?.renderers ||
        renderersResponse?.renderers ||
        [];

      const scenesData = scenesResponse?.scenes || [];

      const sceneLookup = createSceneLookupMap(scenesData);
      const mergedRenderers = mergeRenderersWithScenes(renderers, sceneLookup);

      console.log("✅ mergedRenderers ==========> ", mergedRenderers);

      setAllRenderers(mergedRenderers);

      // ✅ Reset form
      setNewRenderer({
        label: "",
        renderer_type: "Aero",
        renderer_status: "inactive", // ✅ NEW
        aspect_ratio: "16:9",
        resolution: "1920x1080", // ✅ NEW
        frame_rate: 60, // ✅ NEW
        scene_id: "",
        create_preview_renderer: false,
      });

      handleClose();
      alert.success("Renderer created successfully");
    } catch (error) {
      console.error("Error adding renderer:", error);
      // OPUS-1148: surface the backend's specific reason (renderer limit reached,
      // duplicate name, …) rather than a generic message.
      alert.error(error?.message || "Failed to create renderer");
    } finally {
      setLoading(false);
    }
  };

  const handleFullscreen = () => {
    if (multiViewRef.current?.requestFullscreen) {
      multiViewRef.current.requestFullscreen();
    }
  };

  return (
    <>
      <Grid container spacing={1} sx={{ height: "100vh" }}>
        <Grid size={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <DashboardBreadcrumbs />
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Menu
                id="filter-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleFilterClose}
                MenuListProps={{
                  "aria-labelledby": "filter-button",
                }}
                PaperProps={{
                  sx: {
                    minWidth: 180,
                  },
                }}
              >
                <MenuItem
                  onClick={() => handleFilterChange(false)}
                  selected={!showOnlyMyRenderers}
                  sx={{
                    "&.Mui-selected": {
                      backgroundColor: "#333",
                    },
                    "&:hover": {
                      backgroundColor: "#333",
                    },
                  }}
                >
                  <ListItemIcon>
                    <Group fontSize="small" sx={{ color: "#fff" }} />
                  </ListItemIcon>
                  <ListItemText>All renderers</ListItemText>
                </MenuItem>

                <MenuItem
                  onClick={() => handleFilterChange(true)}
                  selected={showOnlyMyRenderers}
                  disabled={!hasUserRenderers}
                  sx={{
                    "&.Mui-selected": {
                      backgroundColor: "#333",
                    },
                    "&:hover": {
                      backgroundColor: "#333",
                    },
                    "&.Mui-disabled": {
                      opacity: 0.5,
                      color: "rgba(255,255,255,0.5)",
                    },
                  }}
                >
                  <ListItemIcon>
                    <Person
                      fontSize="small"
                      sx={{
                        color: showOnlyMyRenderers ? "#FF455C" : "#fff",
                        opacity: hasUserRenderers ? 1 : 0.5,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText>My renderers</ListItemText>
                </MenuItem>
              </Menu>
              {canCreateRenderer && (
                <OpusButton variant="primary" icon="plus" onClick={handleOpen}>Add Renderer</OpusButton>
              )}
            </Box>
          </Box>
        </Grid>

        <Grid size={12} sx={{ height: "100%" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "calc(100% - 67px)",
              width: "100%",
              p: 2,
              background: "var(--surface-1)",
              border: "1px solid var(--border)",
              borderRadius: "var(--r-lg)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              {/* OPUS-913: organisation selection moved to the global sidebar selector */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  variant="body2"
                  sx={{
                    mr: 1,
                    color: hasUserRenderers
                      ? "inherit"
                      : "rgba(255,255,255,0.5)",
                  }}
                >
                  {showOnlyMyRenderers ? "My Renderers" : "All Renderers"}
                </Typography>
                <OpusToggle
                  checked={showOnlyMyRenderers}
                  onChange={(v) => handleFilterChange(v)}
                  // OPUS-1148: a specific creator is already a stronger filter than
                  // "my renderers", so disable the toggle while a creator is selected.
                  disabled={!hasUserRenderers || creatorFilter !== "all"}
                  label="My renderers"
                />
              </Box>

              <Divider
                orientation="vertical"
                variant="middle"
                flexItem
                sx={{ ml: 2, mr: 2 }}
              />

              {(canEditRenderer || canDeleteRenderer) && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    Edit
                  </Typography>
                  <OpusToggle
                    checked={editMode}
                    onChange={(v) => setEditMode(v)}
                    disabled={viewMode === "gridView"}
                    label="Edit mode"
                  />
                </Box>
              )}

              {/* Scene/creator filtering + sort are viewing conveniences, not
                  privileged actions — available to anyone who can see the list. */}
              <Divider
                orientation="vertical"
                variant="middle"
                flexItem
                sx={{ ml: 2, mr: 2 }}
              />
              <Box sx={{ minWidth: 180 }}>
                <OpusSelect
                  size="sm"
                  value={selectedSceneFilter}
                  onChange={(v) => handleSceneFilterChange(v)}
                  options={[{ value: "all", label: "All Scenes" }, ...((scenes || []).map((s) => ({ value: s.id, label: s.name })))]}
                />
              </Box>
              {/* OPUS-1045: filter by creator + sort by time/name/creator/scene. */}
              <Box sx={{ minWidth: 160, ml: 1 }}>
                <OpusSelect
                  size="sm"
                  value={creatorFilter}
                  onChange={(v) => setCreatorFilter(v)}
                  options={creatorOptions}
                />
              </Box>
              <Box sx={{ minWidth: 150, ml: 1 }}>
                <OpusSelect
                  size="sm"
                  value={sortBy}
                  onChange={(v) => setSortBy(v)}
                  options={[
                    { value: "newest", label: "Newest first" },
                    { value: "oldest", label: "Oldest first" },
                    { value: "name", label: "Name (A–Z)" },
                    { value: "creator", label: "Creator" },
                    { value: "scene", label: "Scene" },
                  ]}
                />
              </Box>

              {editMode && canDeleteRenderer && (
                <Button
                  disabled={!editMode || selectedRenderer.length === 0}
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={handleOpenDeleteDialog}
                  sx={{ ml: 2 }}
                >
                  Delete selection ({selectedRenderer.length})
                </Button>
              )}

              <Box sx={{ flexGrow: 1 }} />

              {viewMode === "gridView" && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {/* OPUS-1045: pick which renderers appear on the multiviewer. */}
                  <OpusButton
                    size="sm"
                    variant={multiviewerEditMode ? "primary" : "secondary"}
                    icon="check"
                    onClick={() => setMultiviewerEditMode((v) => !v)}
                  >
                    {multiviewerEditMode
                      ? `Done (${multiviewerSelection.length} selected)`
                      : "Select renderers"}
                  </OpusButton>
                  {multiviewerSelection.length > 0 && (
                    <Tooltip title="Show all renderers">
                      <IconButton size="small" onClick={() => setMultiviewerSelection([])}>
                        <Remove />
                      </IconButton>
                    </Tooltip>
                  )}
                  {/* OPUS-1148: save the current multiview as a named preset, or load one. */}
                  {multiviewerPresets.length > 0 && (
                    <Box sx={{ minWidth: 150, ml: 1 }}>
                      <OpusSelect
                        size="sm"
                        value=""
                        onChange={(v) => v && handleLoadPreset(v)}
                        options={[{ value: "", label: "Load preset…" }, ...multiviewerPresets.map((p) => ({ value: p.name, label: p.name }))]}
                      />
                    </Box>
                  )}
                  {multiviewerSelection.length > 0 && (
                    <Box sx={{ ml: 1 }}>
                      <OpusButton size="sm" variant="secondary" icon="plus" onClick={handleSavePreset}>
                        Save preset
                      </OpusButton>
                    </Box>
                  )}
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  <Tooltip title="Fewer columns">
                    <IconButton
                      onClick={() =>
                        setGridColumns((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={gridColumns <= 1}
                      size="small"
                    >
                      <Remove />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="body2" sx={{ mx: 1 }}>
                    {gridColumns}×
                  </Typography>
                  <Tooltip title="More columns">
                    <IconButton
                      onClick={() =>
                        setGridColumns((prev) => Math.min(prev + 1, 6))
                      }
                      disabled={gridColumns >= 6}
                      size="small"
                    >
                      <Add />
                    </IconButton>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  <Tooltip title="Fullscreen">
                    <IconButton onClick={handleFullscreen}>
                      <Fullscreen />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              <Typography variant="body1">
                {filteredRenderers?.length} renderer
                {filteredRenderers?.length !== 1 ? "s" : ""}
              </Typography>

              <Divider
                orientation="vertical"
                variant="middle"
                flexItem
                sx={{ ml: 2, mr: 2 }}
              />

              <OpusSegmented
                size="sm"
                value={viewMode}
                onChange={(v) => setViewMode(v)}
                options={[
                  { value: "list", icon: "rendererList", label: "" },
                  { value: "preview", icon: "monitor", label: "" },
                  { value: "gridView", icon: "gallery", label: "" },
                ]}
              />
            </Box>

            <Box
              sx={{
                overflow: "auto",
                height: viewMode === "gridView" ? "calc(100% - 100px)" : "auto",
                width: "100%",
                // Reserve the scrollbar gutter on both edges so the list keeps
                // equal left/right margins (otherwise the scrollbar eats 8px on
                // the right only, making the right margin look different).
                scrollbarGutter: "stable both-edges",
                "&::-webkit-scrollbar": {
                  width: "8px !important",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(255,255,255,0.3) !important",
                  borderRadius: "4px !important",
                },
              }}
            >
              {loading ? (
                <CardGridSkeleton count={8} label="Loading renderers…" />
              ) : (
                <>
                  {filteredRenderers && filteredRenderers.length > 0 ? (
                    <Box
                      ref={multiViewRef}
                      sx={{
                        display: viewMode === "gridView" ? "grid" : "flex",
                        gridTemplateColumns:
                          viewMode === "gridView"
                            ? `repeat(${gridColumns}, 1fr)`
                            : "none",
                        gap: viewMode === "gridView" ? 2 : 0,
                        flexWrap: "wrap",
                        flexDirection: "column",
                      }}
                    >
                      {(viewMode === "gridView" && !multiviewerEditMode && multiviewerSelection.length > 0
                        ? filteredRenderers.filter((r) => multiviewerSelection.includes(r.renderer_id))
                        : filteredRenderers
                      ).map((renderer, index) => {
                        // A locked renderer may only be edited/deleted by its lock holder or a
                        // super_admin — renderers.lock.bypass no longer waives this (retired
                        // as a bypass mechanism; the permission stays in the catalog but has
                        // no effect here).
                        const lockedByOther = !!(
                          renderer.owner_lock?.locked &&
                          String(renderer.owner_lock.locked_by_user_id || "") !== String(userId || "") &&
                          !isSuperAdmin
                        );
                        return (
                        <Box
                          key={renderer.renderer_id || index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            mb: viewMode === "gridView" ? 0 : 1,
                          }}
                        >
                          {editMode && canDeleteRenderer && (
                            <Tooltip title={lockedByOther ? `Locked by ${renderer.owner_lock?.locked_by_name || "its owner"}` : ""} disableHoverListener={!lockedByOther}>
                              <span>
                                <Checkbox
                                  sx={{ mr: 1 }}
                                  size="small"
                                  disabled={lockedByOther}
                                  checked={selectedRenderer.includes(
                                    renderer.renderer_id,
                                  )}
                                  onChange={(e) =>
                                    handleRendererSelection(e, renderer.renderer_id)
                                  }
                                />
                              </span>
                            </Tooltip>
                          )}
                          {/* OPUS-1045: multiviewer tile picker (gridView build mode). */}
                          {viewMode === "gridView" && multiviewerEditMode && (
                            <Checkbox
                              sx={{ mr: 1 }}
                              size="small"
                              checked={multiviewerSelection.includes(renderer.renderer_id)}
                              onChange={(e) =>
                                setMultiviewerSelection((prev) =>
                                  e.target.checked
                                    ? [...prev, renderer.renderer_id]
                                    : prev.filter((id) => id !== renderer.renderer_id),
                                )
                              }
                            />
                          )}
                          <RendererConfig
                            edit={editMode && canEditRenderer && !lockedByOther}
                            view={viewMode}
                            sx={{ flexGrow: 1, width: "100%" }}
                            rendererId={renderer.renderer_id}
                            renderer={renderer}
                            sceneData={renderer.sceneData}
                            sceneList={scenes}
                            rendererTotal={filteredRenderers.length}
                            createdBy={renderer?.created_by?.name}
                            createdAt={renderer?.created_at}
                            viewers={presence[renderer.renderer_id] || []}
                            currentUserId={userId}
                            isSuperAdmin={isSuperAdmin}
                            onToggleOwnerLock={handleToggleOwnerLock}
                          />
                        </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "calc(100vh - 250px)",
                        textAlign: "center",
                      }}
                    >
                      <AspectRatio
                        sx={{
                          fontSize: 60,
                          color: "rgba(255,255,255,0.2)",
                          mb: 2,
                        }}
                      />
                      <Typography
                        variant="h5"
                        sx={{ color: "rgba(255,255,255,0.7)", mb: 1 }}
                      >
                        No renderers found
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "rgba(255,255,255,0.5)",
                          maxWidth: 500,
                          mb: 3,
                        }}
                      >
                        {selectedSceneFilter !== "all"
                          ? "No renderers are available for the selected scene."
                          : showOnlyMyRenderers
                            ? "You don't have any renderers. Try creating one or view all renderers."
                            : "No renderers have been created yet for this organisation."}
                      </Typography>
                      {canCreateRenderer && (
                        <Button
                          variant="outlined"
                          color="seafoamGreen"
                          onClick={handleOpen}
                          startIcon={<AddCircle />}
                        >
                          Create New Renderer
                        </Button>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Create Renderer Modal */}
      <OpusModal
        open={open}
        onClose={handleClose}
        title="Add new renderer"
        subtitle="Create a render output for your organisation."
        icon="rendererList"
        width={600}
        footer={loading ? null : (
          <>
            <OpusButton variant="ghost" onClick={handleClose}>Cancel</OpusButton>
            <OpusButton variant="primary" icon="plus" onClick={handleSubmit}>Submit</OpusButton>
          </>
        )}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularIndeterminate />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
            <OpusField label="Renderer name" required>
              <OpusTextInput value={newRenderer.label} onChange={(v) => setNewRenderer((p) => ({ ...p, label: v }))} placeholder="e.g. Match-Buddy-4-box" icon="rendererList" />
            </OpusField>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <OpusField label="Renderer type" required>
                <OpusSelect value={newRenderer.renderer_type || "Aero"} onChange={(v) => setNewRenderer((p) => ({ ...p, renderer_type: v }))}
                  options={[{ value: "Aero", label: "Aero" }, { value: "OBS", label: "OBS" }, { value: "Viz", label: "Viz" }, { value: "Unreal", label: "Unreal" }]} />
              </OpusField>
              <OpusField label="Renderer status" required>
                <OpusSelect value={newRenderer.renderer_status} onChange={(v) => setNewRenderer((p) => ({ ...p, renderer_status: v }))}
                  options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }, { value: "maintenance", label: "Maintenance" }]} />
              </OpusField>
            </Box>
            <Box component="label" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, p: "12px 14px", border: "1px solid var(--border)", borderRadius: "var(--r-md)", cursor: "pointer" }}>
              <Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>Create preview renderer</Typography>
                <Typography sx={{ fontSize: 12, color: "var(--text-dim)" }}>Provision a dedicated preview output for operators.</Typography>
              </Box>
              <OpusToggle checked={newRenderer.create_preview_renderer} onChange={(v) => setNewRenderer((p) => ({ ...p, create_preview_renderer: v }))} label="Create preview renderer" />
            </Box>
            <OpusField label="Scene" helper="Bind to an existing scene.">
              <OpusSelect value={newRenderer.scene_id} onChange={(v) => setNewRenderer((p) => ({ ...p, scene_id: v }))} placeholder="Select scene…" icon="scene"
                options={(scenes || []).map((s) => ({ value: s.id, label: s.name }))} />
            </OpusField>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
              <OpusField label="Aspect ratio"><OpusTextInput value={newRenderer.aspect_ratio} onChange={(v) => setNewRenderer((p) => ({ ...p, aspect_ratio: v }))} placeholder="16:9" /></OpusField>
              <OpusField label="Resolution"><OpusTextInput value={newRenderer.resolution} onChange={(v) => setNewRenderer((p) => ({ ...p, resolution: v }))} placeholder="1920x1080" /></OpusField>
              <OpusField label="Frame rate"><OpusTextInput value={String(newRenderer.frame_rate)} onChange={(v) => setNewRenderer((p) => ({ ...p, frame_rate: v.replace(/\D/g, "") }))} placeholder="60" /></OpusField>
            </Box>
          </Box>
        )}
      </OpusModal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        PaperProps={{
          style: {
            backgroundColor: "#1e1e1e",
            color: "#fff",
          },
        }}
      >
        <DialogTitle>Confirm Renderer Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#ccc" }}>
            Are you sure you want to delete the selected renderer
            {selectedRenderer.length !== 1 ? "s" : ""}? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="silver">
            Cancel
          </Button>
          <Button
            sx={{ fontWeight: "bold" }}
            onClick={handleBulkDeleteRenderers}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={
              deleteLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {deleteLoading ? "Deleting..." : "Delete selection"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RendererManagement;
